<template>
  <teleport to="body">
    <WdsModal :show="open" :title="$t('Your contract status overview')" @cancel="emit('close')">
      <WdsStack direction-column gap-md>
        <WdsText>
          <div class="tw-font-semibold">
            {{ $t('Your contract can have the following statuses:') }}
          </div>
        </WdsText>

        <WdsStack direction-column gap-md>
          <!-- Offered -->
          <WdsStack align-center justify-center gap-md>
            <div class="tw-w-24 tw-flex-shrink-0 tw-flex">
              <WdsLabel sm primary subtle>
                <WdsStack align-center gap-xs>
                  <div class="tw-w-1.5 tw-h-1.5 tw-bg-indigo-500 tw-rounded-full"></div>
                  <span>{{ $t('Offered') }}</span>
                </WdsStack>
              </WdsLabel>
            </div>
            <WdsText class="tw-flex-1">
              <div class="tw-font-normal">
                {{
                  $t(
                    "You've received an offer. The contract is waiting for you to sign. You may need to complete required onboarding steps first.",
                  )
                }}
              </div>
            </WdsText>
          </WdsStack>

          <WdsDivider />

          <!-- Ready -->
          <WdsStack align-center justify-center gap-md>
            <div class="tw-w-24 tw-flex-shrink-0 tw-flex">
              <WdsLabel sm success subtle>
                <WdsStack align-center gap-xs>
                  <div class="tw-w-1.5 tw-h-1.5 tw-bg-green-600 tw-rounded-full"></div>
                  <span>{{ $t('Ready') }}</span>
                </WdsStack>
              </WdsLabel>
            </div>
            <WdsText class="tw-flex-1">
              <div class="tw-font-normal">
                {{
                  $t(
                    "You've signed the contract. You're ready to begin work on the contract start date.",
                  )
                }}
              </div>
            </WdsText>
          </WdsStack>

          <WdsDivider />

          <!-- Active -->
          <WdsStack align-center justify-center gap-md>
            <div class="tw-w-24 tw-flex-shrink-0 tw-flex">
              <WdsLabel sm success>
                <WdsStack align-center gap-xs>
                  <div class="tw-w-1.5 tw-h-1.5 tw-bg-white tw-rounded-full"></div>
                  <span>{{ $t('Active') }}</span>
                </WdsStack>
              </WdsLabel>
            </div>
            <WdsText class="tw-flex-1">
              <div class="tw-font-normal">
                {{ $t("You're currently working under this contract.") }}
              </div>
            </WdsText>
          </WdsStack>

          <WdsDivider />

          <!-- Terminated -->
          <WdsStack align-center justify-center gap-md>
            <div class="tw-w-24 tw-flex-shrink-0 tw-flex">
              <WdsLabel sm error subtle>
                <WdsStack align-center gap-xs>
                  <div class="tw-w-1.5 tw-h-1.5 tw-bg-red-500 tw-rounded-full"></div>
                  <span>{{ $t('Terminated') }}</span>
                </WdsStack>
              </WdsLabel>
            </div>
            <WdsText class="tw-flex-1">
              <div class="tw-font-normal">
                {{ $t('This contract ended early before the original end date.') }}
              </div>
            </WdsText>
          </WdsStack>

          <WdsDivider />

          <!-- Ended -->
          <WdsStack align-center justify-center gap-md>
            <div class="tw-w-24 tw-flex-shrink-0 tw-flex">
              <WdsLabel sm error subtle>
                <WdsStack align-center gap-xs>
                  <div class="tw-w-1.5 tw-h-1.5 tw-bg-red-500 tw-rounded-full"></div>
                  <span>{{ $t('Ended') }}</span>
                </WdsStack>
              </WdsLabel>
            </div>
            <WdsText class="tw-flex-1">
              <div class="tw-font-normal">
                {{
                  $t(
                    'This contract ended as scheduled. Work is complete. You may still submit payment requests.',
                  )
                }}
              </div>
            </WdsText>
          </WdsStack>

          <WdsDivider />

          <!-- Cancelled -->
          <WdsStack align-center justify-center gap-md>
            <div class="tw-w-24 tw-flex-shrink-0 tw-flex">
              <WdsLabel sm subtle>
                <WdsStack align-center gap-xs>
                  <div class="tw-w-1.5 tw-h-1.5 tw-bg-gray-700 tw-rounded-full"></div>
                  <span>{{ $t('Cancelled') }}</span>
                </WdsStack>
              </WdsLabel>
            </div>
            <WdsText class="tw-flex-1">
              <div class="tw-font-normal">
                {{ $t('This contract was cancelled before work began.') }}
              </div>
            </WdsText>
          </WdsStack>
        </WdsStack>
      </WdsStack>

      <template #footer>
        <WdsStack justify-space-between align-end gap-sm class="tw-w-full">
          <WdsStack>
            <WdsStack direction-column>
              <WdsText class="tw-pl-1">{{ $t('Need more help?') }}</WdsText>
              <WdsButtonGhost secondary href="https://help.worksome.com" target="_blank">
                <WdsStack align-center gap-sm>
                  <span>{{ $t("Visit Worksome's help center") }}</span>
                  <WdsIcon name="arrow-up-right" :size="16" />
                </WdsStack>
              </WdsButtonGhost>
            </WdsStack>
          </WdsStack>

          <WdsStack>
            <WdsButton @click="emit('close')">
              {{ $t('Close') }}
            </WdsButton>
          </WdsStack>
        </WdsStack>
      </template>
    </WdsModal>
  </teleport>
</template>

<script setup lang="ts">
import {
  WdsButton,
  WdsButtonGhost,
  WdsDivider,
  WdsIcon,
  WdsLabel,
  WdsModal,
  WdsStack,
  WdsText,
} from '@worksome/wds-components'

import { $t } from '@/app-bootstrapping/SetUpInternationalization'

withDefaults(
  defineProps<{
    open?: boolean
  }>(),
  {
    open: false,
  },
)

const emit = defineEmits(['close'])
</script>
